import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  apiFetch,
  isSuperadminRole,
  parseJsonResponse,
  apiResponseError,
  USER_PRODUCT_ENTITLEMENT_OPTIONS,
  userProductLabel,
} from '../constants';

const emptyForm = {
  email: '',
  name: '',
  role: 'operator',
  product_entitlement: 'both',
};

const ASSIGNABLE_ROLES = {
  superadmin: ['superadmin', 'admin', 'operator', 'readonly'],
  admin: ['admin', 'operator', 'readonly'],
};

const PLATFORM_TENANT_ID = 'social-engineer';

const ROLE_GROUPS = [
  { key: 'admins', label: 'Admins', roles: ['superadmin', 'admin'] },
  { key: 'operators', label: 'Operators', roles: ['operator'] },
  { key: 'readonly', label: 'Read-only users', roles: ['readonly'] },
];

function roleOptionsForActor(actorRole, currentRole, scopeTenantId = null) {
  const assignable = ASSIGNABLE_ROLES[actorRole] || ASSIGNABLE_ROLES.admin;
  let options = [...assignable];
  if (currentRole === 'superadmin' && !options.includes('superadmin')) {
    options.unshift('superadmin');
  }
  if (scopeTenantId && scopeTenantId !== PLATFORM_TENANT_ID) {
    options = options.filter((role) => role !== 'superadmin');
  }
  return options;
}

function roleLabel(role) {
  if (role === 'superadmin') return 'Superadmin';
  if (role === 'admin') return 'Admin';
  if (role === 'readonly') return 'Read-only';
  return 'Operator';
}

function usersApiPath(scopeTenantId, suffix = '') {
  const query = scopeTenantId ? `?tenant_id=${encodeURIComponent(scopeTenantId)}` : '';
  return `/api/org/users${suffix}${query}`;
}

function UserRow({
  user,
  actorRole,
  actorUserId,
  scopeTenantId,
  isAdminVariant,
  onRoleChange,
  onResetAccessKey,
  onDelete,
}) {
  const isSelf = actorUserId && user.user_id === actorUserId;
  const isSuperadminUser = user.role === 'superadmin';
  const canModify = !isSelf && (isSuperadminRole(actorRole) || !isSuperadminUser);
  const editableRoles = roleOptionsForActor(actorRole, user.role, scopeTenantId);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border border-gray-800 rounded px-3 py-2 text-xs">
      <div>
        <div className="text-white">{user.email}</div>
        {user.name && <div className="text-gray-500">{user.name}</div>}
        {user.product_entitlement && (
          <div className="text-gray-500">{userProductLabel(user.product_entitlement)}</div>
        )}
        {isSelf && <div className="text-gray-600">You</div>}
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <select
          value={user.role}
          onChange={(e) => onRoleChange(user.user_id, e.target.value)}
          disabled={!canModify}
          className="px-2 py-1 bg-black border border-gray-700 rounded text-white disabled:opacity-50"
        >
          {editableRoles.map((role) => (
            <option key={role} value={role}>{roleLabel(role)}</option>
          ))}
        </select>
        {canModify && (
          <>
            <button
              type="button"
              onClick={() => onResetAccessKey(user.user_id, user.email)}
              className={`px-2 py-1 rounded border text-xs ${
                isAdminVariant
                  ? 'border-teal-500/40 text-teal-300 hover:bg-teal-950/40'
                  : 'border-amber-500/40 text-amber-300 hover:bg-amber-950/40'
              }`}
            >
              Regenerate key
            </button>
            <button
              type="button"
              onClick={() => onDelete(user.user_id, user.email)}
              className="px-2 py-1 rounded border border-red-500/50 bg-red-950/40 text-red-300 hover:bg-red-950/70 text-xs font-medium"
            >
              Delete
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function OrgUsersPanel({
  canManageUsers,
  actorRole = 'admin',
  actorUserId = null,
  scopeTenantId = null,
  variant = 'workspace',
  groupByRole = false,
  embedded = false,
}) {
  const isAdminVariant = variant === 'admin';
  const [users, setUsers] = useState([]);
  const [assignableProducts, setAssignableProducts] = useState(['apex_elicit']);
  const [orgProductEntitlement, setOrgProductEntitlement] = useState('apex_elicit');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [revealedAccessKey, setRevealedAccessKey] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteDataAction, setDeleteDataAction] = useState('delete');
  const [reassignToUserId, setReassignToUserId] = useState('');
  const [deleting, setDeleting] = useState(false);

  const creatableRoles = roleOptionsForActor(actorRole, null, scopeTenantId);
  const productChoiceLocked = assignableProducts.length <= 1;
  const productOptions = useMemo(
    () => USER_PRODUCT_ENTITLEMENT_OPTIONS.filter((option) => assignableProducts.includes(option.value)),
    [assignableProducts],
  );

  const groupedUsers = useMemo(() => {
    if (!groupByRole) {
      return null;
    }
    return ROLE_GROUPS.map((group) => ({
      ...group,
      users: users.filter((user) => group.roles.includes(user.role)),
    }));
  }, [groupByRole, users]);

  const loadUsers = useCallback(async () => {
    if (!canManageUsers) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(usersApiPath(scopeTenantId));
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(apiResponseError(response, data, 'Failed to load team members'));
      }
      const products = data.assignable_user_products || ['wraith'];
      setUsers(data.users || []);
      setAssignableProducts(products);
      setOrgProductEntitlement(data.org_product_entitlement || 'wraith');
      setForm((current) => ({
        ...current,
        product_entitlement: products.includes(current.product_entitlement)
          ? current.product_entitlement
          : (products[0] || 'wraith'),
      }));
    } catch (err) {
      setError(err.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, [canManageUsers, scopeTenantId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  if (!canManageUsers) {
    return null;
  }

  const resetForm = () => {
    setForm({
      ...emptyForm,
      product_entitlement: assignableProducts[0] || 'wraith',
    });
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        email: form.email.trim(),
        name: form.name.trim(),
        role: form.role,
        product_entitlement: form.product_entitlement,
      };
      const response = await apiFetch(usersApiPath(scopeTenantId), {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(apiResponseError(response, data, 'Failed to create user'));
      }
      if (data.access_key) {
        setRevealedAccessKey({
          email: data.user.email,
          organizationId: scopeTenantId,
          accessKey: data.access_key,
          product: data.user.product_entitlement,
        });
      } else {
        setSuccess(`Created ${data.user.email}`);
      }
      resetForm();
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleResetAccessKey = async (userId, email) => {
    if (!window.confirm(`Regenerate access key for ${email}? The old key will stop working immediately.`)) return;
    setError(null);
    setSuccess(null);
    try {
      const response = await apiFetch(usersApiPath(scopeTenantId, `/${userId}/reset-access-key`), { method: 'POST' });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(apiResponseError(response, data, 'Failed to regenerate access key'));
      }
      setRevealedAccessKey({
        email,
        organizationId: scopeTenantId,
        accessKey: data.access_key,
      });
    } catch (err) {
      setError(err.message || 'Failed to regenerate access key');
    }
  };

  const handleRoleChange = async (userId, role) => {
    setError(null);
    try {
      const response = await apiFetch(usersApiPath(scopeTenantId, `/${userId}`), {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(apiResponseError(response, data, 'Failed to update user'));
      }
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to update user');
    }
  };

  const handleDelete = async (userId, email) => {
    setDeleteTarget({ userId, email });
    setDeleteDataAction('delete');
    setReassignToUserId('');
    setError(null);
    setSuccess(null);
  };

  const resetDeleteDialog = () => {
    setDeleteTarget(null);
    setDeleteDataAction('delete');
    setReassignToUserId('');
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    resetDeleteDialog();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteDataAction === 'reassign' && !reassignToUserId) {
      setError('Select a user to receive reassigned data.');
      return;
    }
    const target = deleteTarget;
    setDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      const params = new URLSearchParams({ data_action: deleteDataAction });
      if (deleteDataAction === 'reassign') {
        params.set('reassign_to_user_id', reassignToUserId);
      }
      const query = scopeTenantId
        ? `?tenant_id=${encodeURIComponent(scopeTenantId)}&${params.toString()}`
        : `?${params.toString()}`;
      const response = await apiFetch(`/api/org/users/${target.userId}${query}`, { method: 'DELETE' });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(apiResponseError(response, data, 'Failed to delete user'));
      }
      if (deleteDataAction === 'reassign') {
        setSuccess(`Deleted ${target.email} and reassigned their data.`);
      } else {
        setSuccess(`Deleted ${target.email} and removed their data.`);
      }
      setUsers((current) => current.filter((user) => user.user_id !== target.userId));
      resetDeleteDialog();
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const userRowProps = {
    actorRole,
    actorUserId,
    scopeTenantId,
    isAdminVariant,
    onRoleChange: handleRoleChange,
    onResetAccessKey: handleResetAccessKey,
    onDelete: handleDelete,
  };

  return (
    <div className={`${embedded ? '' : 'mb-4'} rounded-lg border p-4 space-y-4 ${
      isAdminVariant
        ? 'bg-gray-950 border-teal-500/20'
        : 'bg-gray-900 border-amber-500/30'
    }`}>
      {!isAdminVariant && (
        <div>
          <h3 className="text-sm font-semibold text-amber-300">Team</h3>
          <p className="text-xs text-gray-500 mt-1">
            Manage operators, read-only viewers, and admins in your organization.
          </p>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-300 bg-red-950/40 border border-red-500/30 rounded px-3 py-2">{error}</div>
      )}
      {success && (
        <div className="text-xs text-green-300 bg-green-950/30 border border-green-500/30 rounded px-3 py-2">{success}</div>
      )}
      {revealedAccessKey && (
        <div className={`text-xs rounded px-3 py-2 space-y-1 ${
          isAdminVariant
            ? 'text-teal-200 bg-teal-950/30 border border-teal-500/30'
            : 'text-amber-200 bg-amber-950/30 border border-amber-500/30'
        }`}>
          <div>Save this access key now — it cannot be retrieved again.</div>
          <div><span className="text-gray-400">Email:</span> {revealedAccessKey.email}</div>
          {revealedAccessKey.organizationId && (
            <div><span className="text-gray-400">Organization ID:</span> {revealedAccessKey.organizationId}</div>
          )}
          {revealedAccessKey.product && (
            <div><span className="text-gray-400">Product:</span> {userProductLabel(revealedAccessKey.product)}</div>
          )}
          <div><span className="text-gray-400">Access Key:</span> <code className="text-white break-all">{revealedAccessKey.accessKey}</code></div>
          <button
            type="button"
            onClick={() => setRevealedAccessKey(null)}
            className={`mt-1 ${isAdminVariant ? 'text-teal-400 hover:text-teal-200' : 'text-amber-400 hover:text-amber-200'}`}
          >
            Dismiss
          </button>
        </div>
      )}

      <form onSubmit={handleCreate} className="space-y-2 border border-gray-800 rounded-lg p-3">
        <div className="text-xs font-medium text-gray-300">Add team member</div>
        <p className="text-[11px] text-gray-500">
          A unique access key is generated automatically. Share Organization ID + access key for sign-in.
        </p>
        {orgProductEntitlement === 'wraith' && (
          <p className="text-[11px] text-teal-300/80">
            This organization is Legend Sovereign only. New users are assigned Sovereign automatically.
          </p>
        )}
        {orgProductEntitlement === 'legend_parley' && (
          <p className="text-[11px] text-teal-300/80">
            This organization is Legend Parley only. New users are assigned Parley automatically.
          </p>
        )}
        {orgProductEntitlement === 'both' && !productChoiceLocked && (
          <p className="text-[11px] text-teal-300/80">
            This organization licenses Sovereign and Parley. Choose which product each user may access.
          </p>
        )}
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email"
          required
          className="w-full px-3 py-1.5 bg-black border border-gray-700 rounded text-xs text-white"
        />
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Display name (optional)"
          className="w-full px-3 py-1.5 bg-black border border-gray-700 rounded text-xs text-white"
        />
        <select
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          className="w-full px-3 py-1.5 bg-black border border-gray-700 rounded text-xs text-white"
        >
          {creatableRoles.map((role) => (
            <option key={role} value={role}>{roleLabel(role)}</option>
          ))}
        </select>
        <div>
          <label className="block text-[11px] text-gray-400 mb-1">Product</label>
          <select
            value={form.product_entitlement}
            onChange={(e) => setForm({ ...form, product_entitlement: e.target.value })}
            disabled={productChoiceLocked}
            className="w-full px-3 py-1.5 bg-black border border-gray-700 rounded text-xs text-white disabled:opacity-60"
          >
            {productOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {productChoiceLocked && (
            <p className="text-[10px] text-gray-500 mt-1">
              Locked to {userProductLabel(assignableProducts[0])} for this organization.
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={creating}
          className={`px-3 py-1.5 text-black text-xs font-medium rounded disabled:opacity-50 ${
            isAdminVariant
              ? 'bg-teal-500 hover:bg-teal-400'
              : 'bg-amber-600/80 hover:bg-amber-500'
          }`}
        >
          {creating ? 'Adding…' : 'Add user'}
        </button>
      </form>

      <div className="space-y-3">
        <div className="text-xs text-gray-400">{loading ? 'Loading…' : `${users.length} member(s)`}</div>
        {groupByRole ? (
          groupedUsers.map((group) => (
            <div key={group.key} className="space-y-2">
              <div className="text-xs font-semibold text-teal-300/90 uppercase tracking-wide">
                {group.label}
                <span className="text-gray-500 font-normal normal-case ml-2">({group.users.length})</span>
              </div>
              {group.users.length === 0 ? (
                <p className="text-[11px] text-gray-600 px-1">No {group.label.toLowerCase()} yet.</p>
              ) : (
                group.users.map((user) => (
                  <UserRow key={user.user_id} user={user} {...userRowProps} />
                ))
              )}
            </div>
          ))
        ) : (
          users.map((user) => (
            <UserRow key={user.user_id} user={user} {...userRowProps} />
          ))
        )}
      </div>

      {deleteTarget && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-4 py-6 overflow-y-auto">
          <div className={`w-full max-w-md rounded-lg border p-4 space-y-4 my-auto ${
            isAdminVariant
              ? 'bg-gray-950 border-teal-500/30'
              : 'bg-gray-900 border-amber-500/30'
          }`}>
            <div>
              <h4 className="text-sm font-semibold text-white">Delete {deleteTarget.email}?</h4>
              <p className="text-xs text-gray-500 mt-1">
                Choose what happens to this user&apos;s consultations, uploads, and knowledge-base files.
              </p>
            </div>

            <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer">
              <input
                type="radio"
                name="delete-data-action"
                value="delete"
                checked={deleteDataAction === 'delete'}
                onChange={() => setDeleteDataAction('delete')}
                className="mt-0.5"
              />
              <span>
                <span className="text-white">Delete user and all their data</span>
                <span className="block text-gray-500 mt-0.5">
                  Removes their sessions, conversations, uploads, and KB files.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer">
              <input
                type="radio"
                name="delete-data-action"
                value="reassign"
                checked={deleteDataAction === 'reassign'}
                onChange={() => setDeleteDataAction('reassign')}
                className="mt-0.5"
              />
              <span>
                <span className="text-white">Reassign data to another user</span>
                <span className="block text-gray-500 mt-0.5">
                  Keeps consultations and KB uploads under a different team member.
                </span>
              </span>
            </label>

            {deleteDataAction === 'reassign' && (
              <select
                value={reassignToUserId}
                onChange={(e) => setReassignToUserId(e.target.value)}
                className="w-full px-3 py-1.5 bg-black border border-gray-700 rounded text-xs text-white"
              >
                <option value="">Select user…</option>
                {users
                  .filter((user) => user.user_id !== deleteTarget.userId && user.status !== 'disabled')
                  .map((user) => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.email}{user.name ? ` (${user.name})` : ''}
                    </option>
                  ))}
              </select>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={closeDeleteDialog}
                disabled={deleting}
                className="px-3 py-1.5 text-xs rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="px-3 py-1.5 text-xs rounded border border-red-500/50 bg-red-950/40 text-red-300 hover:bg-red-950/70 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete user'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
